import React, {useEffect, useRef, useState} from "react";
import {
  Autocomplete,
  GoogleMap,
  useLoadScript,
  Marker,
} from "@react-google-maps/api";
import "./addressselect.css";

interface LocationCoords {
  lat: number;
  lng: number;
}

interface AddressSelectProps {
  value: string;
  onAddressChange: (address: string) => void;
  onLocationSelect: (location: LocationCoords) => void;
  onLocationClear?: () => void;
  initialLocation?: LocationCoords;
  placeholder?: string;
  label?: string;
  error?: string;
  disabled?: boolean;
}

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";
const DEFAULT_CENTER = {lat: 17.385044, lng: 78.486671}; // Hyderabad

const mapContainerStyle = {
  width: "100%",
  height: "200px",
};

// Define libraries array outside component to prevent re-renders
const libraries: ("places")[] = ["places"];

export const AddressSelect: React.FC<AddressSelectProps> = ({
  value,
  onAddressChange,
  onLocationSelect,
  onLocationClear,
  initialLocation,
  placeholder = "Search for your address...",
  label,
  error,
  disabled = false,
}) => {
  // Use useLoadScript instead of LoadScript to prevent multiple API loads
  const {isLoaded, loadError} = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const getValidPosition = (pos?: LocationCoords) => {
    if (pos && typeof pos.lat === "number" && typeof pos.lng === "number") {
      return {lat: pos.lat, lng: pos.lng};
    }
    return DEFAULT_CENTER;
  };

  const [mapCenter, setMapCenter] = useState(getValidPosition(initialLocation));
  const [markerPosition, setMarkerPosition] =
    useState<google.maps.LatLngLiteral | null>(
      initialLocation ? { lat: initialLocation.lat, lng: initialLocation.lng } : null
    );
  const [inputValues, setInputValues] = useState(value);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const confirmedAddressRef = useRef<string>(value || "");

  useEffect(() => {
    if(initialLocation?.lat && initialLocation?.lng){
      const newPosition = { lat: initialLocation.lat, lng: initialLocation.lng };
      setMarkerPosition(newPosition);
      setMapCenter(newPosition);
      // Pan map to marker position if map is already loaded
      if (mapRef.current) {
        mapRef.current.panTo(newPosition);
        mapRef.current.setZoom(15);
      }
    }
  }, [initialLocation])

  useEffect(() => {
    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
      if (mapRef.current) {
        google.maps.event.clearInstanceListeners(mapRef.current);
      }
    };
  }, []);

  // Sync inputValues when value prop changes (for restoration from session)
  useEffect(() => {
    setInputValues(value);
    if (value) {
      confirmedAddressRef.current = value;
    }
  }, [value]);

  const repositionPacContainer = () => {
    if (!inputRef.current) return;

    const rect = inputRef.current.getBoundingClientRect();
    const containers =
      document.querySelectorAll<HTMLDivElement>(".pac-container");

    containers.forEach((container) => {
      const isMobile = window.innerWidth <= 768;

      // Calculate available space
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const maxHeight = Math.min(300, Math.max(150, spaceBelow - 20));

      container.style.position = "fixed";
      container.style.top = `${rect.bottom}px`;
      container.style.left = `${rect.left}px`;
      container.style.width = `${rect.width}px`;
      container.style.zIndex = "99999";
      container.style.maxHeight = `${maxHeight}px`;
      container.style.overflowY = "auto";
      container.style.overflowX = "hidden";
      container.style.boxShadow = "0 2px 6px rgba(0,0,0,0.3)";
      container.style.backgroundColor = "#fff";

      // Mobile-specific adjustments
      if (isMobile) {
        container.style.fontSize = "16px"; // Prevents zoom on iOS
        (container.style as any).webkitOverflowScrolling = "touch";
      }
    });
  };

  useEffect(() => {
    const handleScrollOrResize = () => {
      repositionPacContainer();
    };

    // Debounce for better performance
    let timeoutId: ReturnType<typeof setTimeout>;
    const debouncedHandler = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScrollOrResize, 50);
    };

    window.addEventListener("scroll", debouncedHandler, true);
    window.addEventListener("resize", debouncedHandler, true);
    window.addEventListener("orientationchange", handleScrollOrResize, true);

    // Some mobile browsers only fire visualViewport events on keyboard / UI chrome changes
    const vv = (window as any).visualViewport as VisualViewport | undefined;
    if (vv) {
      vv.addEventListener("resize", handleScrollOrResize);
      vv.addEventListener("scroll", handleScrollOrResize);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("scroll", debouncedHandler, true);
      window.removeEventListener("resize", debouncedHandler, true);
      window.removeEventListener(
        "orientationchange",
        handleScrollOrResize,
        true,
      );
      if (vv) {
        vv.removeEventListener("resize", handleScrollOrResize);
        vv.removeEventListener("scroll", handleScrollOrResize);
      }
    };
  }, []);

  // Observe the DOM for the creation of the .pac-container so we can position it immediately
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      // Only reposition if pac-container nodes are added
      const pacContainerAdded = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node instanceof HTMLElement &&
            (node.classList.contains("pac-container") ||
              node.querySelector(".pac-container")),
        ),
      );
      if (pacContainerAdded) {
        setTimeout(repositionPacContainer, 100);
      }
    });
    observer.observe(document.body, {childList: true, subtree: false});
    return () => observer.disconnect();
  }, []);

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry?.location) {
        const newLatLng = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        setMapCenter(newLatLng);
        setMarkerPosition(newLatLng);
        onLocationSelect(newLatLng);

        let selectedAddress = "";
        if (
          place.name &&
          place.formatted_address &&
          !place.formatted_address.includes(place.name)
        ) {
          selectedAddress = `${place.name}, ${place.formatted_address}`;
        } else {
          selectedAddress = place.formatted_address || place.name || "";
        }

        setInputValues(selectedAddress);
        onAddressChange(selectedAddress);
        confirmedAddressRef.current = selectedAddress;
      }
    }
  };

  const onAutocompleteLoad = (
    autocomplete: google.maps.places.Autocomplete,
  ) => {
    autocompleteRef.current = autocomplete;
  };

  const onMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;

    // 🔥 force center after load
    map.panTo(mapCenter);
    map.setZoom(15);
  };

  if (loadError) {
    return (
      <div className="address-select__error">
        Error loading Google Maps
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="address-select__loading">
        Loading...
      </div>
    );
  }

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="address-select__error">
        Google Maps API key not configured
      </div>
    );
  }

  return (
    <div className="address-select">
      {label && <label className="address-select__label">{label}</label>}

      <style>{`
        .pac-container {
          z-index: 99999 !important;
          position: fixed !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.4) !important;
          background-color: #fff !important;
          border: 1px solid #ccc !important;
          border-radius: 4px !important;
          margin-top: 2px !important;
          pointer-events: auto !important;
        }
        .pac-container:after {
          display: none !important;
        }
        .pac-item {
          padding: 10px 12px !important;
          cursor: pointer !important;
          font-size: 16px !important;
          line-height: 1.5 !important;
          border-bottom: 1px solid #f0f0f0 !important;
        }
        .pac-item:hover, .pac-item-selected {
          background-color: #f5f5f5 !important;
        }
        .pac-item-query {
          font-size: 16px !important;
        }
        @media (max-width: 768px) {
          .pac-item {
            padding: 14px 12px !important;
            font-size: 16px !important;
            min-height: 48px !important;
          }
        }
      `}</style>

      <div style={{position: "relative", zIndex: 1}}>
        <Autocomplete
          onLoad={onAutocompleteLoad}
          onPlaceChanged={handlePlaceChanged}
          options={{
            componentRestrictions: {country: "in"},
            // Remove types restriction OR use establishment to get businesses
            // Removing types entirely gives the broadest results like Google Maps
            // types: ["establishment"],
            
            // Add location bias to improve relevance
            bounds: new google.maps.LatLngBounds(
              new google.maps.LatLng(17.285044, 78.386671), // Southwest
              new google.maps.LatLng(17.485044, 78.586671)  // Northeast
            ),
            fields: ["formatted_address", "geometry", "name", "place_id"],
          }}>
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <input
              ref={inputRef}
              value={inputValues}
              onChange={(e) => {
                const newValue = e.target.value;
                setInputValues(newValue);
                onAddressChange(newValue);

                // If user edits the text away from the confirmed selection, clear the location
                if (confirmedAddressRef.current && newValue !== confirmedAddressRef.current) {
                  confirmedAddressRef.current = "";
                  setMarkerPosition(null);
                  onLocationClear?.();
                }
              }}
              onFocus={() => setTimeout(repositionPacContainer, 100)}
              placeholder={placeholder}
              disabled={disabled}
              style={{
                width: "100%",
                padding: "12px 16px",
                marginBottom: "12px",
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
                fontSize: "14px",
                position: "relative",
                zIndex: 1002,
                backgroundColor: "#ffffff",
                fontFamily: "inherit",
                boxSizing: "border-box",
              }}
            />
          </div>
        </Autocomplete>

        {error && <span className="address-select__error" style={{ display: 'block', marginBottom: '8px' }}>{error}</span>}

        {/* Google Map */}
        <GoogleMap
          mapContainerStyle={{
            ...mapContainerStyle,
            position: "relative",
            zIndex: 1,
          }}
          center={mapCenter}
          zoom={15}
          onLoad={onMapLoad}
          options={{
            disableDefaultUI: true,
            zoomControl: false,
            scrollwheel: false,
            draggable: false,
          }}>
          {markerPosition && <Marker position={markerPosition} />}
        </GoogleMap>
      </div>

      {/* Selected Location Display */}
      {inputValues && (
        <div className="address-select__selected-location">
          <div className="address-select__selected-icon">🏠</div>
          <div className="address-select__selected-content">
            <h4 className="address-select__selected-title">
              Selected Location
            </h4>
            <p className="address-select__selected-text">{inputValues}</p>
          </div>
        </div>
      )}
    </div>
  );
};